import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

interface AgeDropdownProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

// Generate age options from <18 to 64, and 65+
const generateAgeOptions = () => {
  const options = [
    { id: 17, label: '<18', emoji: '👶' }
  ];
  
  // Add ages 19-64 individually
  for (let age = 19; age <= 64; age++) {
    options.push({ 
      id: age, 
      label: age.toString(),
      emoji: age < 30 ? '👨‍💼' : 
             age < 45 ? '👨‍💻' : 
             age < 55 ? '👨‍🏫' : '👨‍🦳'
    });
  }
  
  // Add 65+ as the last option
  options.push({ id: 65, label: '65+', emoji: '👴' });
  
  return options;
};

const ageOptions = generateAgeOptions();

export const AgeDropdown: React.FC<AgeDropdownProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const selectedAge = ageOptions.find(option => option.id === value) || ageOptions[1]; // Default to 19
  
  // Filter options based on search query
  const filteredOptions = searchQuery
    ? ageOptions.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : ageOptions;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left text-sm h-10 ${className}`}
        type="button"
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedAge.emoji}</span>
          <span className="text-gray-700 text-sm">{selectedAge.label}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20 max-h-60 overflow-auto">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search age..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="py-1">
            {filteredOptions.map(option => (
              <button
                key={option.id}
                onClick={() => {
                  onChange(option.id);
                  setIsOpen(false);
                  setSearchQuery('');
                }}
                className={`w-full text-left px-4 py-2 ${
                  option.id === value
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
                type="button"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{option.emoji}</span>
                  <div className="font-medium">{option.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 